import { Link } from "react-router-dom";
import styled from "styled-components";
import { Box, Flex, Text } from "rebass/styled-components";
import { HiShieldCheck, HiVideoCamera } from "react-icons/hi";
import { IoCheckmarkSharp } from "react-icons/io5";
import { FiInfo } from "react-icons/fi";

import { AnimatedTooltip } from "MVVM/Components/AnimatedTooltip";
import { ClipQualityIcon } from "MVVM/Components/Icons";
import { useUser } from "MVVM/Hooks/useUser";
import {
  useClipQualitySettingsModal,
  useCs2HudSettingsModal,
} from "MVVM/Hooks/useClipQualitySettingsModal";
import {
  useClipQualityConfigs,
  ClipQualityConfigSettings,
} from "MVVM/Hooks/useClipQualityConfigs";
import { NewBadge } from "MVVM/Components/ClipQualitySettings/ClipQualityBubble";
import cs2Logo from "MVVM/Assets/GameLogos/cs2.svg";
import { analytics } from "MVVM/Analytics";
import { usePortal } from "MVVM/Hooks/usePortal";
import { SignatureSelectorModal } from "MVVM/Components/Signatures";
import { URLS } from "MVVM/Utilities/Maps";
import { useAutoSave } from "./useAutoSave";
import { useAutoClip } from "./useAutoClip";

// Same preset floors as AUTOCLIP_PRESETS so "capture everything, save only
// highlights" reads as one scale.
const AUTO_SAVE_PRESETS = [
  {
    threshold: 30,
    label: "Everything",
    description: "Save every clip automatically",
  },
  {
    threshold: 110,
    label: "Solid plays",
    description: "Save multi-kills, clutches, and better",
  },
  {
    threshold: 160,
    label: "Highlights only",
    description: "Only save your very best moments",
  },
];

const AUTOCLIP_PRESETS = [
  {
    threshold: 30,
    label: "Everything",
    description: "Clip every play worth watching",
  },
  {
    threshold: 110,
    label: "Solid plays",
    description: "Multi-kills, clutches, and better",
  },
  {
    threshold: 160,
    label: "Highlights only",
    description: "Just your very best moments",
  },
];

interface ClipSettingsBodyProps {
  /** Rendered inside the modal, which has no panel heading of its own. */
  showHeader?: boolean;
  /** Modal closes itself before handing off to another modal. Noop on a page. */
  onNavigateAway?: () => void;
  source?: string;
}

const describeQualityConfig = (
  settings: ClipQualityConfigSettings,
  blurLabelMap: Record<number, string>,
): string => {
  const { resolution, framerate, frameBlendBlur } = settings;
  let text = `Your clips render at ${resolution} ${framerate}fps`;
  if (frameBlendBlur > 1) {
    const blurLabel = blurLabelMap[frameBlendBlur] ?? `${frameBlendBlur}x`;
    text += ` ${blurLabel.toLowerCase()} blur`;
  }
  return text;
};

export const ClipSettingsBody = ({
  showHeader = false,
  onNavigateAway,
  source = "clip-settings",
}: ClipSettingsBodyProps) => {
  const { allstarUser } = useUser();
  const { open: openClipQuality } = useClipQualitySettingsModal();
  const { open: openCs2Hud } = useCs2HudSettingsModal();
  const { configs, currentConfig, blurLabelMap } = useClipQualityConfigs();

  const currentConfigObj = currentConfig
    ? configs.find((c) => c.configName === currentConfig)
    : undefined;
  const {
    optimisticValue,
    optimisticThreshold: autoSaveThreshold,
    loading,
    isEligible,
    handleToggle,
    handleThresholdSelect: handleAutoSaveThresholdSelect,
    upsellOpen,
    UpsellPortal,
  } = useAutoSave(source);
  const {
    enabled: autoClipEnabled,
    threshold: autoClipThreshold,
    loading: autoClipLoading,
    isEligible: autoClipEligible,
    handleToggle: handleAutoClipToggle,
    handleThresholdSelect,
    upsellOpen: autoClipUpsellOpen,
    UpsellPortal: AutoClipUpsellPortal,
  } = useAutoClip(source);

  const avatar = allstarUser?.user?.avatar;
  const username = allstarUser?.user?.username;

  const {
    portalOpen: signatureSelectorOpen,
    setPortalOpen: setSignatureSelectorOpen,
    Portal: SignatureSelectorPortal,
  } = usePortal(
    <SignatureSelectorModal
      onSave={() => setSignatureSelectorOpen(false)}
      onCloseClick={() => setSignatureSelectorOpen(false)}
    />,
  );

  const handleOpenQuality = () => {
    onNavigateAway?.();
    setTimeout(() => openClipQuality(), 150);
  };

  const handleOpenHud = () => {
    onNavigateAway?.();
    setTimeout(() => openCs2Hud(), 150);
  };

  const handleOpenSignature = () => {
    analytics.track("Signature - Open Modal", {
      where: "Clip Settings",
      type: "Manage",
    });
    setSignatureSelectorOpen(true);
  };

  return (
    <>
      {showHeader && (
        <HeaderRow>
          {avatar && <Avatar src={avatar} alt={username || ""} />}
          <HeaderText>
            <Title>Clipping Settings</Title>
            <Subtitle>Manage your clipping settings</Subtitle>
          </HeaderText>
        </HeaderRow>
      )}

      <CardGrid $flush={!showHeader}>
        <Card onClick={handleOpenHud}>
          <CardIcon>
            <GameLogo src={cs2Logo} alt="" />
          </CardIcon>
          <CardTitle>
            HUD &amp; Crosshair
            <NewBadge>New</NewBadge>
          </CardTitle>
          <CardDescription>
            Choose which HUD elements and crosshair show on your Counter-Strike
            clips
          </CardDescription>
        </Card>

        <Card onClick={handleOpenQuality}>
          <CardIcon>
            <ClipQualityIcon size={20} />
          </CardIcon>
          <CardTitle>Clip Quality</CardTitle>
          <CardDescription>
            {currentConfigObj
              ? describeQualityConfig(currentConfigObj.settings, blurLabelMap)
              : "Set the resolution and framerate your clips render at"}
          </CardDescription>
        </Card>

        <Card onClick={handleOpenSignature}>
          <CardIcon>
            <SignatureIcon
              src={`${process.env.REACT_APP_S3_CLIP_BUCKET}/static/loadouts/sig.svg`}
              alt=""
            />
          </CardIcon>
          <CardTitle>My Signature</CardTitle>
          <CardDescription>
            Choose the cards and music that play on your clips
          </CardDescription>
        </Card>
      </CardGrid>

      <SettingsList>
        <SettingRow>
          <SettingInfo>
            <SettingLabel>
              <AutoCaptureIcon size={16} />
              <Accent>Autocapture</Accent> my matches
              <NewBadge>New</NewBadge>
              <AnimatedTooltip
                content="After each match ends, Allstar automatically clips your best plays. No clipping required."
                position="right"
                maxWidth={220}
              >
                <InfoIconLink to={URLS.AUTOCAPTURE} onClick={onNavigateAway}>
                  <FiInfo size={13} />
                </InfoIconLink>
              </AnimatedTooltip>
            </SettingLabel>
            <SettingDescription>
              Get your best moments clipped automatically after every match
            </SettingDescription>
          </SettingInfo>
          <Checkbox
            $checked={autoClipEnabled}
            $loading={autoClipLoading}
            onClick={handleAutoClipToggle}
          >
            {autoClipEnabled && <IoCheckmarkSharp size={12} />}
          </Checkbox>
        </SettingRow>

        {autoClipEnabled && autoClipEligible && (
          <FilterGroup>
            {AUTOCLIP_PRESETS.map((preset) => (
              <FilterOption
                key={preset.threshold}
                $selected={preset.threshold === autoClipThreshold}
                onClick={() => handleThresholdSelect(preset.threshold)}
              >
                <RadioDot $selected={preset.threshold === autoClipThreshold} />
                <FilterInfo>
                  <FilterLabel>{preset.label}</FilterLabel>
                  <FilterDescription>{preset.description}</FilterDescription>
                </FilterInfo>
              </FilterOption>
            ))}
          </FilterGroup>
        )}

        <SettingRow>
          <SettingInfo>
            <SettingLabel>
              <HiShieldCheck size={16} />
              Autosave my clips
              <AnimatedTooltip
                content="When enabled, your new clips are saved automatically so they won't expire after 72 hours"
                position="right"
                maxWidth={220}
              >
                <InfoIconWrap>
                  <FiInfo size={13} />
                </InfoIconWrap>
              </AnimatedTooltip>
            </SettingLabel>
            <SettingDescription>
              Keep every clip you make instead of losing it after 72 hours
            </SettingDescription>
          </SettingInfo>
          <Checkbox
            $checked={optimisticValue}
            $loading={loading}
            onClick={handleToggle}
          >
            {optimisticValue && <IoCheckmarkSharp size={12} />}
          </Checkbox>
        </SettingRow>

        {optimisticValue && isEligible && (
          <FilterGroup>
            {AUTO_SAVE_PRESETS.map((preset) => (
              <FilterOption
                key={preset.threshold}
                $selected={preset.threshold === autoSaveThreshold}
                onClick={() => handleAutoSaveThresholdSelect(preset.threshold)}
              >
                <RadioDot $selected={preset.threshold === autoSaveThreshold} />
                <FilterInfo>
                  <FilterLabel>{preset.label}</FilterLabel>
                  <FilterDescription>{preset.description}</FilterDescription>
                </FilterInfo>
              </FilterOption>
            ))}
          </FilterGroup>
        )}
      </SettingsList>

      {upsellOpen && UpsellPortal}
      {autoClipUpsellOpen && AutoClipUpsellPortal}
      {signatureSelectorOpen && SignatureSelectorPortal}
    </>
  );
};

const HeaderRow = styled(Flex)`
  align-items: center;
  gap: 14px;
`;

const Avatar = styled.img`
  border-radius: 50%;
  flex-shrink: 0;
  height: 40px;
  object-fit: cover;
  width: 40px;
`;

const HeaderText = styled(Flex)`
  flex-direction: column;
`;

const Title = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-family: Barlow;
  font-size: 24px;
  font-weight: 700;
`;

const Subtitle = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  font-family: Barlow;
  font-size: 15px;
  margin-top: 4px;
`;

const CardGrid = styled(Box)<{ $flush?: boolean }>`
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  margin-top: ${({ $flush }) => ($flush ? "0" : "24px")};
`;

const Card = styled.button`
  align-items: flex-start;
  background: ${({ theme }) => theme.colors.navy};
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha10};
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 18px 16px;
  text-align: left;
  transition:
    border-color 0.15s ease,
    background 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.navy50};
    border-color: ${({ theme }) => theme.colors.envy};
  }
`;

const CardIcon = styled(Flex)`
  align-items: center;
  color: ${({ theme }) => theme.colors.chalk};
  height: 24px;
  justify-content: center;
`;

const CardTitle = styled(Flex)`
  align-items: center;
  color: ${({ theme }) => theme.colors.ice};
  font-family: Barlow;
  font-size: 17px;
  font-weight: 600;
  gap: 8px;
`;

const CardDescription = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  font-family: Barlow;
  font-size: 14px;
  line-height: 1.4;
`;

const SettingsList = styled(Flex)`
  flex-direction: column;
  margin-top: 20px;
`;

const AutoCaptureIcon = styled(HiVideoCamera)`
  color: ${({ theme }) => theme.colors.envy};
  flex-shrink: 0;
`;

const Accent = styled.span`
  color: ${({ theme }) => theme.colors.envy};
`;

const GameLogo = styled.img`
  height: 20px;
  width: 20px;
`;

const SignatureIcon = styled.img`
  height: 19px;
  width: 19px;
`;

const SettingRow = styled(Flex)`
  align-items: center;
  gap: 16px;
  justify-content: space-between;
  padding: 12px 0;
  text-align: left;
  width: 100%;
`;

const SettingInfo = styled(Flex)`
  flex-direction: column;
  gap: 2px;
`;

const SettingLabel = styled(Flex)`
  align-items: center;
  color: ${({ theme }) => theme.colors.ice};
  font-family: Barlow;
  font-size: 17px;
  font-weight: 600;
  gap: 6px;
`;

const SettingDescription = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  font-family: Barlow;
  font-size: 14px;
`;

const InfoIconWrap = styled(Flex)`
  align-items: center;
  color: ${({ theme }) => theme.colors.chalk};
  cursor: help;
  opacity: 0.6;
  transition: opacity 0.15s;

  &:hover {
    opacity: 1;
  }
`;

const InfoIconLink = styled(InfoIconWrap.withComponent(Link))`
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.envy};
  }
`;

const Checkbox = styled.div<{ $checked: boolean; $loading?: boolean }>`
  align-items: center;
  background-color: ${({ $checked, theme }) =>
    $checked ? theme.colors.envy : "transparent"};
  border: 2px solid
    ${({ $checked, theme }) =>
      $checked ? theme.colors.envy : theme.colors.chalkAlpha20};
  border-radius: 4px;
  color: white;
  cursor: pointer;
  display: flex;
  flex-shrink: 0;
  height: 20px;
  justify-content: center;
  opacity: ${({ $loading }) => ($loading ? 0.5 : 1)};
  transition: all 150ms ease;
  width: 20px;

  &:hover {
    border-color: ${({ theme }) => theme.colors.envy};
  }
`;

const FilterGroup = styled(Flex)`
  flex-direction: column;
  gap: 2px;
  padding: 4px 0 8px 0;
`;

const FilterOption = styled(Flex)<{
  $selected?: boolean;
}>`
  align-items: flex-start;
  border-radius: 8px;
  cursor: pointer;
  gap: 10px;
  padding: 10px 12px;
  transition: background 0.15s;

  &:hover {
    background: ${({ theme }) => theme.colors.chalkAlpha05};
  }
`;

const RadioDot = styled.div<{ $selected?: boolean }>`
  background: ${({ $selected, theme }) =>
    $selected ? theme.colors.envy : "transparent"};
  border: 2px solid
    ${({ $selected, theme }) =>
      $selected ? theme.colors.envy : theme.colors.chalkAlpha20};
  border-radius: 50%;
  flex-shrink: 0;
  height: 16px;
  margin-top: 2px;
  transition: all 150ms ease;
  width: 16px;
`;

const FilterInfo = styled(Flex)`
  flex-direction: column;
  gap: 1px;
`;

const FilterLabel = styled(Flex)`
  align-items: center;
  color: ${({ theme }) => theme.colors.ice};
  font-family: Barlow;
  font-size: 15px;
  font-weight: 500;
  gap: 8px;
`;

const FilterDescription = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  font-family: Barlow;
  font-size: 13px;
`;
